import { motion } from "framer-motion";
import { useMemo } from "react";
import enAacharyData from "~/data/about-us/aachary.json";
import gujAacharyData from "~/data/about-us/aachary_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

// Hero Section
const HeroSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-8"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{aacharyData.title}</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic max-w-2xl mx-auto", medium)}>{aacharyData.subTitle}</p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div whileHover={{ scale: 1.01 }} className="relative">
          {/* Decorative icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -top-6 left-6 md:left-12 w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </motion.div>

          <div className="p-8 md:p-12 rounded-2xl" style={BACKGROUND_IMAGE_STYLE}>
            <h2 className={cn("text-xl md:text-2xl font-bold text-primaryColor mb-6", medium)}>{aacharyData.content.q1.title}</h2>

            {aacharyData.content.q1.description.length > 0 &&
              aacharyData.content.q1.description.map((desc, index) => (
                <p
                  key={index}
                  className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
                >
                  {desc}
                </p>
              ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Three Resolutions Section
const ResolutionsSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const resolutionIcons = {
    temples: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),

    establish_aacharyas: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),

    compose_scriptures: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-8 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {aacharyData.content.swaminarayanResolutions.title}
          </h2>
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed max-w-4xl mx-auto", normalInter)}>
            {aacharyData.content.swaminarayanResolutions.description.p1}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-20">
          {aacharyData.content.swaminarayanResolutions.description.resolutions.map((resolution, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="p-8 rounded-xl border-2 border-primaryColor/40 shadow-lg hover:border-primaryColor transition-all duration-300 text-center group"
              style={BACKGROUND_IMAGE_STYLE}
            >
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="w-16 h-16 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md text-white"
              >
                {resolutionIcons[resolution.icon as keyof typeof resolutionIcons]}
              </motion.div>

              <h3 className={cn("text-xl font-bold text-primaryColor mb-2 group-hover:text-textColor transition-colors", medium)}>
                {resolution.title}
              </h3>

              <p className={cn("text-textSecondaryColor text-sm", normalInter)}>{resolution.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <ImageSection aacharyData={aacharyData} />
      </div>
    </motion.section>
  );
};

// Image Section
const ImageSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/30 hover:border-primaryColor/60 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <motion.img
            transition={{ duration: 0.5 }}
            src={aacharyData.content.swaminarayanResolutions.description.mainImage.src}
            alt={aacharyData.content.swaminarayanResolutions.description.mainImage.alt}
            className="w-full aspect-auto object-contain"
            loading="lazy"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

// Two Divine Thrones Section
const TwoDivineThronesSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 md:mb-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {aacharyData.content.twoDivineThrones.title}
          </h2>
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}>
            {aacharyData.content.twoDivineThrones.description.p1}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {aacharyData.content.twoDivineThrones.description.temples.map((division: any, index: number) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className={`p-8 rounded-2xl border-2 border-primaryColor/40 shadow-xl hover:border-primaryColor transition-all duration-300`}
              style={BACKGROUND_IMAGE_STYLE}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center mr-4 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
                <h3 className={cn("text-xl md:text-2xl font-bold text-primaryColor", medium)}>{division.title}</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>
                    {aacharyData.content.twoDivineThrones.labels.deity}
                  </span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.deity}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>
                    {aacharyData.content.twoDivineThrones.labels.temple}
                  </span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.temple}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>
                    {aacharyData.content.twoDivineThrones.labels.aacharya}
                  </span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.aacharya}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>
                    {aacharyData.content.twoDivineThrones.labels.father}
                  </span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.father}</span>
                </div>
              </div>

              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-6 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}>
            {aacharyData.content.twoDivineThrones.description.p2}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {aacharyData.content.twoDivineThrones.description.aacharyas.map((division: any, index: number) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className={`p-4 rounded-2xl border-2 border-primaryColor/40 shadow-xl hover:border-primaryColor transition-all duration-300`}
              style={BACKGROUND_IMAGE_STYLE}
            >
              {division.name && (
                <div className="flex items-center mb-4">
                  <h3 className={cn("text-xl md:text-xl font-bold text-primaryColor", medium)}>{division.name}</h3>
                </div>
              )}

              <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>{division.description}</p>

              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-6 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Sanskrit Verse Section
const SanskritVerseSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            From Shikshapatri
          </h2>
          <p className={cn("text-textSecondaryColor text-sm md:text-base italic", normalInter)}>{aacharyData.content.sanskritVerse.description}</p>
        </motion.div>

        <motion.div whileHover={{ scale: 1.01 }} className="relative">
          {/* Sanskrit verse card */}
          <div className="p-8 md:p-12 rounded-2xl border-4 border-primaryColor/30 shadow-xl mb-6" style={BACKGROUND_IMAGE_STYLE}>
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ delay: 0.4 }}
              >
                <p
                  className={cn("text-primaryColor text-xl md:text-2xl leading-relaxed font-semibold mb-6", normalInter)}
                  dangerouslySetInnerHTML={{ __html: aacharyData.content.sanskritVerse.shloka }}
                />
              </motion.div>

              <motion.div
                initial={{ scaleX: 0 }}
                animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6"
              />

              <motion.div initial={{ opacity: 0 }} animate={isVisible ? { opacity: 1 } : { opacity: 0 }} transition={{ delay: 0.8 }}>
                <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed italic", normalInter)}>
                  {aacharyData.content.sanskritVerse.translation}
                </p>
              </motion.div>
            </div>
          </div>

          {/* Decorative corner elements */}
          <div className="absolute -top-4 -left-4 w-8 h-8 border-t-4 border-l-4 border-primaryColor rounded-tl-lg" />
          <div className="absolute -top-4 -right-4 w-8 h-8 border-t-4 border-r-4 border-primaryColor rounded-tr-lg" />
          <div className="absolute -bottom-4 -left-4 w-8 h-8 border-b-4 border-l-4 border-primaryColor rounded-bl-lg" />
          <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-4 border-r-4 border-primaryColor rounded-br-lg" />
        </motion.div>
      </div>
    </motion.section>
  );
};

// a living tradition
const LivingTraditionSection = ({ aacharyData }: { aacharyData: any }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {aacharyData.content.livingTradition.title}
          </h2>
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}>
            {aacharyData.content.livingTradition.description}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

function Aachary() {
  const lang = useLanguageParams();
  const aacharyData = useMemo(() => {
    return lang === "gu" ? gujAacharyData : enAacharyData;
  }, [lang]);
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      {/* py-8 md:py-16 */}
      <div className="w-full">
        <HeroSection aacharyData={aacharyData} />
        <IntroductionSection aacharyData={aacharyData} />
        <ResolutionsSection aacharyData={aacharyData} />
        <TwoDivineThronesSection aacharyData={aacharyData} />
        {/* <DateSection /> */}
        <SanskritVerseSection aacharyData={aacharyData} />
        <LivingTraditionSection aacharyData={aacharyData} />
      </div>
    </div>
  );
}

export default Aachary;
